import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { gradeAPI, sheetAPI, shipmentAPI, branchAPI } from '../api';
import { useTheme } from '../contexts/ThemeContext';
import { confirmToast } from '../components/ConfirmToast';
import toast from 'react-hot-toast';

const GRADE_COLORS = ['#22c55e', '#f59e0b', '#f97316', '#ef4444', '#8b5cf6', '#0ea5e9'];

// ─── Пересортица по количеству ───────────────────────────────────────────────
//
// Когда бирок в цеху нет, сканировать нечего. Кладовщик и так работает не с
// листом, а со стопкой: «вот эта ЛДСП, вот столько листов ушло в брак». Экран
// повторяет этот порядок — выбрать товар, выбрать сорт, написать число.
//
// Какой именно лист станет вторым сортом, не знает никто: система берёт самые
// старые, потому что так же считает и отгрузка. Иначе бы остаток и документы
// разошлись.
function ByCount({ grades, onDone }) {
  const [rows, setRows]   = useState([]);
  const [pick, setPick]   = useState(null);   // выбранная пара «товар + сорт»
  const [to, setTo]       = useState(null);   // новый сорт
  const [qty, setQty]     = useState('');
  const [busy, setBusy]   = useState(false);
  const [q, setQ]         = useState('');

  const load = useCallback(() => {
    shipmentAPI.available()
      .then(r => setRows(r.data.results || []))
      .catch(() => toast.error(t('Остаток не загрузился')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setPick(null); setTo(null); setQty(''); };

  const n = Math.max(0, parseInt(qty, 10) || 0);
  const over = pick && n > pick.available;

  const apply = async () => {
    if (!pick || !to || !n) return;
    if (String(to.id) === String(pick.grade_type_id)) return;
    const ok = await confirmToast(
      `${pick.product_name}: ${n} шт из «${pick.grade_name}» в «${to.name}»?`,
      { confirmText: 'Перевести' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await sheetAPI.regrade({
        product_id:          pick.nomenclature_id,
        from_grade_type_id:  pick.grade_type_id,
        grade_type_id:       Number(to.id),
        quantity:            n,
      });
      toast.success(`✅ ${data.updated} шт → «${data.grade_name}»`);
      reset();
      load();
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  // Список короткий, но растёт: на складе десятки позиций, и пролистывать их
  // в перчатках дольше, чем набрать три буквы.
  const shown = rows.filter(r => {
    if (!q.trim()) return true;
    const s2 = (r.product_name + ' ' + r.grade_name).toLowerCase();
    return q.toLowerCase().split(/\s+/).every(w => s2.includes(w));
  });

  const CARD = {
    background: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 16, padding: '18px 22px',
  };

  if (!pick) {
    return (
      <div style={{ width: '100%', maxWidth: 720 }}>
        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
          Что пересортировать
        </div>
        <div style={{ color: 'var(--dim)', fontSize: 14, marginBottom: 16 }}>
          Выберите товар и сорт, из которого переводите.
        </div>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('Поиск по названию…')}
          style={{
            width: '100%', padding: '14px 18px', marginBottom: 12,
            background: 'var(--card)', border: '2px solid var(--border)',
            borderRadius: 12, color: 'var(--text)', fontSize: 17,
            outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
          }} />
        {shown.length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 15, padding: '18px 2px' }}>
            {rows.length ? 'Ничего не нашлось' : 'На складе пусто'}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map(r => (
            <button key={`${r.nomenclature_id}-${r.grade_type_id}`}
              onClick={() => { setPick(r); setTo(null); setQty(''); }}
              className="touch-btn"
              style={{
                ...CARD, padding: '14px 18px', display: 'flex', gap: 12,
                alignItems: 'center', cursor: 'pointer', textAlign: 'left',
                fontFamily: 'inherit', color: 'var(--text)', width: '100%',
              }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>
                  {r.product_name}
                </span>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--dim)', marginTop: 2 }}>
                  {r.grade_name}{r.is_defect ? ' · брак' : ''}
                </span>
              </span>
              <span style={{ fontSize: 22, fontWeight: 800, whiteSpace: 'nowrap' }}>
                {r.available}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 720 }}>
      <div style={{ ...CARD, marginBottom: 18 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{pick.product_name}</div>
        <div style={{ color: 'var(--dim)', fontSize: 14, marginTop: 4 }}>
          сейчас «{pick.grade_name}» · на складе {pick.available} шт
        </div>
      </div>

      <div style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.06em',
                    marginBottom: 10 }}>
        В какой сорт
      </div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 18,
                    gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
        {grades.filter(g => String(g.id) !== String(pick.grade_type_id)).map((g, i) => (
          <button key={g.id} onClick={() => setTo(g)} className="touch-btn" style={{
            padding: '20px 10px', borderRadius: 14, cursor: 'pointer',
            fontSize: 17, fontWeight: 800, fontFamily: 'inherit',
            background: to && to.id === g.id ? GRADE_COLORS[i % GRADE_COLORS.length] : 'var(--card)',
            color: to && to.id === g.id ? '#fff' : 'var(--text)',
            border: `3px solid ${GRADE_COLORS[i % GRADE_COLORS.length]}`,
          }}>{g.name}</button>
        ))}
      </div>

      <div style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '.06em',
                    marginBottom: 10 }}>
        Сколько листов
      </div>
      <input type="text" inputMode="numeric" value={qty} autoFocus
        onChange={e => setQty(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
        onFocus={e => e.target.select()} placeholder="0"
        style={{
          width: '100%', padding: '18px 24px', textAlign: 'center',
          background: 'var(--card)', borderRadius: 16, color: 'var(--text)',
          fontSize: 32, fontWeight: 800, outline: 'none', boxSizing: 'border-box',
          border: `3px solid ${over ? '#ef4444' : 'var(--accent)'}`,
          fontFamily: 'inherit',
        }} />
      {over && (
        <div style={{ color: '#ef4444', fontSize: 14, marginTop: 8 }}>
          На складе только {pick.available} шт
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        <button onClick={reset} className="touch-btn" style={{
          flex: 1, padding: '18px 10px', borderRadius: 14, cursor: 'pointer',
          background: 'var(--card)', border: '2px solid var(--border)',
          color: 'var(--muted)', fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
        }}>Отмена</button>
        <button onClick={apply} disabled={busy || !to || !n || over}
          className="touch-btn" style={{
            flex: 2, padding: '18px 10px', borderRadius: 14,
            border: 'none', fontSize: 17, fontWeight: 800, fontFamily: 'inherit',
            cursor: (to && n && !over) ? 'pointer' : 'not-allowed',
            background: (to && n && !over) ? '#22c55e' : 'var(--border)',
            color: (to && n && !over) ? '#fff' : 'var(--dim)',
          }}>{busy ? '…' : 'Перевести'}</button>
      </div>
    </div>
  );
}

// ─── Терминал пересортицы листов ─────────────────────────────────────────────
//
// Раньше это была вкладка внутри страницы склада: узкая колонка, выпадающий
// список сортов и накопительный список отсканированного. На планшете в цеху,
// в перчатках, по такому экрану не попасть.
//
// Здесь один лист за раз, как у пресса: большое поле сканирования по центру,
// сорт — крупными кнопками, подтверждение, и экран сам возвращается к ожиданию
// следующего листа. Свой сорт среди кнопок подсвечен и нажатие по нему ничего
// не делает — это не ошибка, а «этот лист трогать не надо».
//
// Журнал сбоку — общий, а не «что я отсканировал»: листы перебивают с
// нескольких планшетов сразу, и приёмщик должен видеть работу соседней смены.

export default function SheetTerminal() {
  const nav = useNavigate();
  const { isDark, toggle } = useTheme();

  const [grades, setGrades] = useState([]);
  // Пакет: пузырь на пяти листах одной смены — это одно событие, а не пять.
  // Причина и фотографии у них общие, переписывать их пять раз значит
  // заставлять человека делать лишнее. Один лист — пакет из одного.
  const [basket, setBasket]   = useState([]);   // [{id, code, product, grade_name}]
  const [reasons, setReasons] = useState([]);
  const [reason, setReason]   = useState(null);
  const [note, setNote]       = useState('');
  const [photos, setPhotos]   = useState([]);   // File[]
  const [sending, setSending] = useState(false);
  const [code, setCode]     = useState('');
  const [sheet, setSheet]   = useState(null);   // найденный лист
  const [pick, setPick]     = useState(null);   // выбранный новый сорт
  const [busy, setBusy]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [log, setLog]       = useState([]);
  // Печатать бирки в цеху может быть нечем. Тогда сканировать попросту нечего,
  // и сорт меняют так же, как его считают глазами: вот этот ЛДСП, вот столько
  // листов, вот в какой сорт. Какой именно лист — не знает никто.
  const [noLabel, setNoLabel] = useState(false);
  const inputRef = useRef(null);

  const focus = useCallback(() => {
    // Сканер печатает в активное поле: фокус нельзя терять после каждого шага
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const loadLog = useCallback(() => {
    sheetAPI.gradeLog(20).then(r => setLog(r.data.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    // scope=sheet — только сорта листа. «Брак плёнки» и «Брак ДСП» это брак
    // МАТЕРИАЛА: его отмечают до пресса, когда листа ещё нет, и складу при
    // пересортице предлагать их нельзя.
    //
    // «Не высший» тоже убираем: это счётчик пресса на всю смену — оператор
    // им говорит, сколько листов не высшие, а какой сорт у КОНКРЕТНОГО
    // листа, решают здесь. Поставить листу «не высший» значит не ответить
    // на единственный вопрос этого экрана.
    gradeAPI.getGradeTypes({ active: 'true', scope: 'sheet' })
      .then(r => setGrades((r.data || []).filter(g => !g.is_press_hint)))
      .catch(() => toast.error(t('Типы сортов не загрузились')));
    // Причина обязательна: пузырь — это температура, непроклей — давление,
    // царапина — погрузчик. Одно и то же число означает три разных решения.
    sheetAPI.defectReasons()
      .then(r => setReasons(r.data || []))
      .catch(() => setReasons([]));
    // Список цехов — все разрешённые, а правило берём у того цеха, на который
    // смотрят: иначе галочка одного цеха включала бы режим «без бирки» в другом.
    branchAPI.getList()
      .then(r => {
        const viewed = sessionStorage.getItem('selected_branch') || 'all';
        const list = (r.data || []).filter(b => viewed === 'all' || String(b.id) === viewed);
        setNoLabel(list.some(b => b.grade_without_label));
      })
      .catch(() => {});
    loadLog();
    focus();
  }, [loadLog, focus]);

  // Журнал общий — соседний планшет мог перебить лист минуту назад
  useEffect(() => {
    const id = setInterval(loadLog, 20000);
    return () => clearInterval(id);
  }, [loadLog]);

  const reset = () => { setSheet(null); setPick(null); setCode(''); focus(); };

  const scan = async (raw) => {
    const qr = (raw || '').trim();
    if (!qr) return;
    setBusy(true);
    try {
      const { data } = await sheetAPI.lookup(qr);
      setCode('');
      setSheet(data);
      setPick(null);
      // Второй раз тот же лист — не ошибка, просто уже в пакете
      setBasket(b => (b.some(x => String(x.id) === String(data.id)) ? b : b));
    } catch {
      setCode('');
      toast.error(t('❌ Лист не найден'));
      focus();
    } finally { setBusy(false); }
  };

  const blocked = sheet && (
    sheet.is_shipped ? 'Лист отгружен — сорт менять нельзя'
      : sheet.pallet_short ? `Лист в паллете ${sheet.pallet_short} — сначала выньте его`
      : null);

  // Лист больше не меняет сорт сразу. Он ложится в пакет, а сорт ему
  // поставит директор — если согласится. Брак это деньги, списанные с
  // выпуска, и решение о них принимал один человек, у которого стопка перед
  // глазами и смена кончается.
  const addToBasket = () => {
    if (!sheet || !pick) return;
    if (String(pick.id) === String(sheet.grade_type_id)) return;
    setBasket(b => {
      if (b.some(x => String(x.id) === String(sheet.id))) {
        toast(`${sheet.code || sheet.qr_short} уже в пакете`);
        return b;
      }
      return [...b, {
        id: sheet.id, code: sheet.code || sheet.qr_short,
        product: sheet.product_name || '', grade_name: sheet.grade_name || '',
      }];
    });
    reset();
  };

  const dropFromBasket = (id) =>
    setBasket(b => b.filter(x => String(x.id) !== String(id)));

  const sendPacket = async () => {
    if (!basket.length || !pick || !reason) return;
    setSending(true);
    try {
      const { data } = await sheetAPI.gradeRequest({
        grade_type_id: Number(pick.id),
        reason_id: Number(reason.id),
        sheet_ids: basket.map(x => x.id),
        note: note.trim(),
      });
      // Снимки идут отдельными запросами: браузер шлёт их multipart-ом, и
      // мешать их с JSON значит усложнять обе стороны.
      for (const f of photos) {
        try { await sheetAPI.gradeRequestPhoto(data.id, f); }
        catch { toast.error(`Снимок ${f.name} не загрузился`); }
      }
      toast.success(`📨 Отправлено директору: ${basket.length} листов`);
      setBasket([]); setReason(null); setNote(''); setPhotos([]); setPick(null);
      loadLog(); focus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось отправить');
    } finally { setSending(false); }
  };

  // Журнал общий: рядом стоят строки двух приёмщиков, и «Отменить» легко
  // нажать по инерции на чужой. Поэтому спрашиваем подтверждение и называем
  // в вопросе лист, направление и того, кто это сделал.
  const revert = async (row) => {
    const who = row.mine ? '' : ` (сделал ${row.by})`;
    const ok = await confirmToast(
      `Отменить пересортицу листа ${row.code}${who}? Он вернётся с «${row.to}» на «${row.from}».`,
      { confirmText: 'Отменить пересортицу', danger: !row.mine });
    if (!ok) { focus(); return; }
    setSaving(true);
    try {
      await sheetAPI.revertGrade(row.id);
      toast.success(`↩ ${row.code}: возвращён на «${row.from}»`);
      loadLog();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
    } finally { setSaving(false); focus(); }
  };

  const gradeColor = (i) => GRADE_COLORS[i % GRADE_COLORS.length];
  const sameGrade = pick && sheet && String(pick.id) === String(sheet.grade_type_id);

  const HEAD_BTN = {
    width: 42, height: 42, borderRadius: 10, border: '1px solid var(--border)',
    background: 'var(--card)', color: 'var(--muted)', cursor: 'pointer',
    fontSize: 17, fontFamily: 'inherit',
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Шапка ── */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
        borderBottom: '1px solid var(--border)', background: 'var(--card)', flexShrink: 0,
      }}>
        <button onClick={() => nav('/warehouse')} className="touch-btn" style={HEAD_BTN} title={t('Назад на склад')}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.02em' }}>🏷 Пересортица листов</div>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>
            {sessionStorage.getItem('user_name') || 'Кладовщик'} · Склад ГП
            {noLabel && ' · по количеству, без бирки'}
          </div>
        </div>
        <button onClick={toggle} className="touch-btn" style={HEAD_BTN} title={t('Тема')}>{isDark ? '☀' : '☾'}</button>
      </header>

      <div className="sheet-term">
        {/* ── Центр: скан и сорта ── */}
        <main className="sheet-term-main">
          {noLabel ? (
            <ByCount grades={grades} onDone={loadLog} />
          ) : !sheet ? (
            <div style={{ width: '100%', maxWidth: 620, textAlign: 'center' }}>
              <div style={{ fontSize: 64, marginBottom: 8 }}>📷</div>
              <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
                Сканируйте бирку листа
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 14, marginBottom: 22 }}>
                Один лист за раз. После подтверждения экран сам ждёт следующий.
              </div>
              <input
                ref={inputRef} value={code} disabled={busy} autoFocus
                onChange={e => setCode(e.target.value)}
                onBlur={() => setTimeout(focus, 80)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); scan(code); } }}
                placeholder={busy ? '…' : 'Код листа'}
                style={{
                  width: '100%', padding: '22px 24px', textAlign: 'center',
                  background: 'var(--card)', border: '3px solid var(--accent)',
                  borderRadius: 16, color: 'var(--text)', fontSize: 30, fontWeight: 700,
                  fontFamily: 'monospace', letterSpacing: 2, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10 }}>
                💡 Сканер сам нажимает Enter
              </div>
            </div>
          ) : (
            <div style={{ width: '100%', maxWidth: 720 }}>
              {/* Карточка листа */}
              <div style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 16, padding: '18px 22px', marginBottom: 18,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: 22, fontWeight: 800,
                    background: 'var(--input)', borderRadius: 8, padding: '4px 12px',
                  }}>{sheet.code || sheet.qr_short}</span>
                  <span style={{
                    background: 'var(--accent-weak)', color: 'var(--accent)',
                    borderRadius: 8, padding: '5px 14px', fontSize: 15, fontWeight: 800,
                  }}>{sheet.grade_name}</span>
                  <button onClick={reset} className="touch-btn" style={{
                    marginLeft: 'auto', background: 'var(--input)', border: '1px solid var(--border)',
                    borderRadius: 10, color: 'var(--muted)', padding: '8px 16px',
                    fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                  }}>✕ Отмена</button>
                </div>
                <div style={{ fontSize: 21, fontWeight: 700, marginTop: 12, lineHeight: 1.3 }}>
                  {sheet.product_name || '—'}
                </div>
                {sheet.texture_name && (
                  <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 3 }}>{sheet.texture_name}</div>
                )}
                {blocked && (
                  <div style={{
                    marginTop: 12, background: '#ef444418', border: '1px solid #ef4444',
                    borderRadius: 10, padding: '10px 14px', color: '#ef4444',
                    fontSize: 15, fontWeight: 700,
                  }}>🔒 {blocked}</div>
                )}
              </div>

              {!blocked && (
                <>
                  <div style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                    ВЫБЕРИТЕ СОРТ
                  </div>
                  <div className="sheet-term-grades">
                    {grades.map((g, i) => {
                      const own = String(g.id) === String(sheet.grade_type_id);
                      const on  = pick && String(pick.id) === String(g.id);
                      const c   = gradeColor(i);
                      return (
                        <button
                          key={g.id}
                          onClick={() => { setPick(g); focus(); }}
                          className="touch-btn"
                          style={{
                            background: on ? c : 'var(--card)',
                            border: '2px solid ' + (on ? c : (own ? 'var(--accent)' : 'var(--border)')),
                            borderRadius: 14, color: on ? '#fff' : 'var(--text)',
                            padding: '20px 10px', fontSize: 19, fontWeight: 800,
                            cursor: 'pointer', fontFamily: 'inherit',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                          }}>
                          <span>{g.name}</span>
                          {/* Свой сорт видно сразу — по нему просто нечего менять */}
                          {own && (
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              color: on ? 'rgba(255,255,255,.85)' : 'var(--accent)',
                            }}>сейчас</span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={addToBasket}
                    disabled={saving || !pick || sameGrade}
                    className="touch-btn"
                    style={{
                      width: '100%', marginTop: 18, padding: '20px 0',
                      background: (!pick || sameGrade) ? 'var(--card)' : 'var(--accent)',
                      border: (!pick || sameGrade) ? '1px solid var(--border)' : 'none',
                      borderRadius: 14, color: (!pick || sameGrade) ? 'var(--dim)' : '#fff',
                      fontSize: 20, fontWeight: 800, fontFamily: 'inherit',
                      cursor: (!pick || sameGrade) ? 'not-allowed' : 'pointer',
                    }}>
                    {sameGrade ? 'Это его текущий сорт — менять нечего'
                      : pick ? `➕ В пакет: «${pick.name}»`
                      : 'Выберите сорт'}
                  </button>
                  <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 8, textAlign: 'center', lineHeight: 1.5 }}>
                    Сорт поменяется не сейчас: пакет уходит директору, и лист
                    станет «{pick ? pick.name : '…'}», только если он согласится.
                  </div>
                </>
              )}

              {blocked && (
                <button onClick={reset} className="touch-btn" style={{
                  width: '100%', padding: '18px 0', background: 'var(--accent)',
                  border: 'none', borderRadius: 14, color: '#fff',
                  fontSize: 18, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                }}>Следующий лист</button>
              )}
            </div>
          )}
        </main>

        {/* ── Пакет на утверждение ──
            Причина обязательна, снимки — нет: телефон есть не всегда, а
            пузырь виден и словами. Но без снимка директор утверждает
            вслепую, и в подписи это сказано прямо. */}
        <aside className="sheet-term-log">
          {basket.length > 0 && (
            <div style={{
              background: 'var(--card)', border: '2px solid var(--accent)',
              borderRadius: 14, padding: 14, marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontWeight: 800, fontSize: 15 }}>📦 Пакет</span>
                <span style={{
                  background: 'var(--accent)', color: '#fff', borderRadius: 10,
                  padding: '2px 9px', fontSize: 12, fontWeight: 800,
                }}>{basket.length}</span>
                <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
                  → {pick ? pick.name : 'сорт не выбран'}
                </span>
              </div>

              <div style={{ maxHeight: 150, overflowY: 'auto', marginBottom: 10 }}>
                {basket.map(x => (
                  <div key={x.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 0', borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
                      {x.code}
                    </span>
                    <span style={{
                      color: 'var(--dim)', fontSize: 11.5, flex: 1, minWidth: 0,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{x.product}</span>
                    <button onClick={() => dropFromBasket(x.id)} style={{
                      background: 'none', border: 'none', color: '#ef4444',
                      cursor: 'pointer', fontSize: 15, lineHeight: 1,
                    }}>✕</button>
                  </div>
                ))}
              </div>

              <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                ПРИЧИНА — обязательно
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {reasons.map(rs => {
                  const on = reason && String(reason.id) === String(rs.id);
                  return (
                    <button key={rs.id} onClick={() => setReason(rs)} className="touch-btn"
                      style={{
                        background: on ? 'var(--accent)' : 'var(--bg)',
                        border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
                        borderRadius: 10, color: on ? '#fff' : 'var(--text)',
                        padding: '8px 12px', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                      {rs.icon ? rs.icon + ' ' : ''}{rs.name}
                    </button>
                  );
                })}
              </div>

              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder="Примечание — необязательно"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '9px 11px',
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 10, color: 'var(--text)', fontSize: 13,
                  marginBottom: 10, fontFamily: 'inherit',
                }} />

              <label style={{
                display: 'block', border: '1px dashed var(--border)', borderRadius: 10,
                padding: '10px 12px', textAlign: 'center', cursor: 'pointer',
                color: 'var(--muted)', fontSize: 13, marginBottom: 10,
              }}>
                📷 {photos.length ? `Снимков: ${photos.length}` : 'Добавить фото дефекта'}
                <input type="file" accept="image/*" multiple capture="environment"
                  onChange={e => {
                    setPhotos(p => [...p, ...Array.from(e.target.files || [])]);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }} />
              </label>
              {photos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {photos.map((f, i) => (
                    <span key={i} style={{
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '4px 8px', fontSize: 11,
                      color: 'var(--muted)', display: 'inline-flex', gap: 6,
                    }}>
                      {String(f.name).slice(0, 14)}
                      <span onClick={() => setPhotos(p => p.filter((_, j) => j !== i))}
                        style={{ color: '#ef4444', cursor: 'pointer' }}>✕</span>
                    </span>
                  ))}
                </div>
              )}

              <button onClick={sendPacket} className="touch-btn"
                disabled={sending || !reason || !pick}
                style={{
                  width: '100%', padding: '15px 0', borderRadius: 12,
                  background: (!reason || !pick) ? 'var(--bg)' : '#22c55e',
                  border: (!reason || !pick) ? '1px solid var(--border)' : 'none',
                  color: (!reason || !pick) ? 'var(--dim)' : '#fff',
                  fontSize: 16, fontWeight: 800, fontFamily: 'inherit',
                  cursor: (!reason || !pick) ? 'not-allowed' : 'pointer',
                }}>
                {sending ? 'Отправка…'
                  : !pick ? 'Выберите сорт'
                  : !reason ? 'Выберите причину'
                  : `📨 Директору на утверждение (${basket.length})`}
              </button>
              <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 7, lineHeight: 1.5 }}>
                До решения директора листы остаются прежним сортом, но в
                отгрузку и в паллету не идут.
              </div>
            </div>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <span style={{ fontWeight: 800, fontSize: 15 }}>🕘 История</span>
            <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
              последние {log.length}
            </span>
          </div>

          {log.length === 0 ? (
            <div style={{ color: 'var(--dim)', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>
              Пока пусто
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {log.map(r => (
                <div key={r.id} style={{
                  background: 'var(--card)',
                  // Чужая строка отбита сбоку — видно, что это не твоя работа
                  border: '1px solid var(--border)',
                  borderLeft: r.mine ? '3px solid var(--accent)' : '3px solid var(--warning)',
                  borderRadius: 10, padding: '10px 12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13 }}>{r.code}</span>
                    <span style={{ color: 'var(--dim)', fontSize: 11, marginLeft: 'auto' }}>
                      {new Date(r.at).toLocaleString('ru-RU', {
                        day: '2-digit', month: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 2px', lineHeight: 1.3 }}>
                    {r.product}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {r.is_revert ? '↩' : '🏷'} {r.from} → <b>{r.to}</b>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span style={{
                      color: r.mine ? 'var(--dim)' : 'var(--warning)',
                      fontSize: 11, fontWeight: r.mine ? 400 : 700,
                    }}>{r.mine ? r.by : `${r.by} — не вы`}</span>
                    {r.can_revert && (
                      <button onClick={() => revert(r)} disabled={saving} className="touch-btn" style={{
                        marginLeft: 'auto', background: 'none', border: '1px solid var(--border)',
                        borderRadius: 8, color: 'var(--muted)', fontSize: 11,
                        padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit',
                      }}>↩ Отменить</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
